import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { AuthorizeService } from 'src/api-authorization/authorize.service';
import { Ladder } from 'src/models/ladder.model';
import { Player } from 'src/models/player.model';
import { UserService } from 'src/services/user.service';
import { AdminDialogSetLevelComponent } from './dialogAdminSetLevel.component';
import { AdminDialogSetLadderComponent } from './dialogAdminSetLadder.component';

@Component({
  selector: 'admin-paid',
  templateUrl: './paid.component.html',
  styleUrls: ['paid.component.scss']
})
export class AdminPaidComponent implements OnInit {
  public busy: boolean = false;
  public players: Player[] = [];
  public ladders: Ladder[] = [];
  public ladderId: string = "";
  public msg: string;
  public paidIds: string[] = [];
  public selectedPlayerKeys: Set<string> = new Set();

  constructor(private authorizeService: AuthorizeService,
    private userSvc: UserService, private http: HttpClient,
    private dialog: MatDialog,
    private routerLink: Router) {
    this.busy = false;
  }

  ngOnInit(): void {
    this.http.get<Ladder[]>('/ladders').subscribe(
      (resp) => {
        this.ladders.length = 0;
        for (const l of resp) {
          this.ladders.push(new Ladder(l));
        }
      }
    )

  }

  public fetchPlayers(value: string, preserveScroll = false) {
    const scrollY = preserveScroll ? window.scrollY : undefined;
    this.http.get<Player[]>(`/admin/getallplayers/${value}`).subscribe(
      (resp: Player[]) => {
        console.log(resp);
        this.players.length = 0;
        for (let p of resp) {
          this.players.push(new Player(p));
        }
        this.selectedPlayerKeys.clear();
        if (preserveScroll && scrollY !== undefined) {
          setTimeout(() => window.scrollTo(0, scrollY), 0);
        }
      }
    );
  }

  public ladderChanged(value: string) {
    this.selectedPlayerKeys.clear();
    this.fetchPlayers(value);
  }

  private playerKey(id: string, levelId: number): string {
    return `${id}:${levelId}`;
  }

  public isSelected(id: string, levelId: number): boolean {
    return this.selectedPlayerKeys.has(this.playerKey(id, levelId));
  }

  public toggleSelected(id: string, levelId: number) {
    const key = this.playerKey(id, levelId);
    if (this.selectedPlayerKeys.has(key)) {
      this.selectedPlayerKeys.delete(key);
    } else {
      this.selectedPlayerKeys.add(key);
    }
  }

  public allSelected(): boolean {
    return this.players.length > 0 && this.players.every(p => this.isSelected(p.id, p.levelId));
  }

  public toggleSelectAll() {
    if (this.allSelected()) {
      this.selectedPlayerKeys.clear();
    } else {
      this.players.forEach(p => this.selectedPlayerKeys.add(this.playerKey(p.id, p.levelId)));
    }
  }

  public get selectedCount(): number {
    return this.selectedPlayerKeys.size;
  }

  private getSelectedPlayers(): Player[] {
    return this.players.filter(p => this.isSelected(p.id, p.levelId));
  }

  public bulkLevelDialog() {
    const selected = this.getSelectedPlayers();
    if (selected.length === 0) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.restoreFocus = false;
    const dlg = this.dialog.open(AdminDialogSetLevelComponent, dialogConfig);
    dlg.afterClosed().subscribe(
      (level: string) => {
        if (level != null && level != '') {
          if (!confirm(`Move ${selected.length} player(s) to level ${level}?`)) {
            return;
          }

          const requests = selected.map(p => this.http.post('/admin/setlevel', {
            UserId: p.id,
            LevelId: p.levelId,
            LadderId: this.ladderId,
            NewLevelId: parseInt(level, 10)
          }));

          forkJoin(requests).subscribe(
            () => {
              this.msg = `Success - moved ${selected.length} player(s) to level ${level}`;
              setTimeout(() => this.msg = null, 5000);
              this.fetchPlayers(this.ladderId, true);
            },
            (err) => { this.msg = "ERROR: " + err; console.error(err); }
          );
        }
      }
    );
  }

  public bulkLadderDialog() {
    const selected = this.getSelectedPlayers();
    if (selected.length === 0) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.restoreFocus = false;
    const dlg = this.dialog.open(AdminDialogSetLadderComponent, dialogConfig);
    dlg.afterClosed().subscribe(
      (newladder: string) => {
        if (newladder != null && newladder != '') {
          if (!confirm(`Move ${selected.length} player(s) to ladder ${newladder}?`)) {
            return;
          }

          const requests = selected.map(p => this.http.post('/admin/setladder', {
            UserId: p.id,
            LevelId: p.levelId,
            LadderId: this.ladderId,
            NewLadderId: newladder
          }));

          forkJoin(requests).subscribe(
            () => {
              this.msg = `Success - moved ${selected.length} player(s) to ladder ${newladder}`;
              setTimeout(() => this.msg = null, 5000);
              this.fetchPlayers(this.ladderId, true);
            },
            (err) => { this.msg = "ERROR: " + err; console.error(err); }
          );
        }
      }
    );
  }

  public markPaid(evt: any, id: string, levelId) {
    evt.preventDefault();
    const user = this.players.find(f => f.id == id);
    if (confirm(`Are you sure you want to mark ${user.getFullName()} paid?`)) {
      this.paidIds.push(id);
      this.http.post('/admin/markpaid', {
        UserId: id,
        LevelId: levelId,
        LadderId: this.ladderId,
        paid: true
      }).subscribe(
        (resp) => { this.msg = "Success"; setTimeout(() => this.msg = null, 5000); this.fetchPlayers(this.ladderId); },
        (err) => { this.msg = err; console.error(err); }
      )
    }
  }
  public markUnPaid(evt: any, id: string, levelId) {
    evt.preventDefault();
    // remove this user
    this.paidIds = this.paidIds.filter(f => f != id);

    this.http.post('/admin/markpaid', {
      UserId: id,
      LevelId: levelId,
      LadderId: this.ladderId,
      paid: false
    }).subscribe(
      (resp) => { this.fetchPlayers(this.ladderId); },
      (err) => { this.msg = err; console.error(err); }
    )
  }
  public dropLadder(evt: any, id: string, levelId) {
    evt.preventDefault();
    const user = this.players.find(f => f.id == id);
    if (confirm(`Are you sure you want to drop ${user.getFullName()}?`)) {
      this.http.post('/admin/DropUser', {
        UserId: id,
        LevelId: levelId,
        LadderId: this.ladderId
      }).subscribe(
        (resp) => { this.msg = "Success"; setTimeout(() => this.msg = null, 5000); this.fetchPlayers(this.ladderId); },
        (err) => { this.msg = "ERROR: " + err; console.error(err); }
      )
    }
  }
  public levelDialog(evt: any, id: string, levelId) {
    evt.preventDefault();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.restoreFocus = false;
    var dlg = this.dialog.open(AdminDialogSetLevelComponent, dialogConfig);
    //const user = this.players.find(f => f.id == id);
    dlg.afterClosed().subscribe(
      (level: string) => {
        if( level != null && level != '')
        {
          this.http.post('/admin/setlevel',
            {
              UserId: id,
              LevelId: levelId,
              LadderId: this.ladderId,
              NewLevelId: parseInt(level, 10)
            }).subscribe(
              (resp) => { this.msg = "Success"; setTimeout(() => this.msg = null, 5000); this.fetchPlayers(this.ladderId, true); },
              (err) => { this.msg = "ERROR: " + err; console.error(err); }
            )
        }
      }
    );
  }
  public ladderDialog(evt: any, id: string, levelId) {
    evt.preventDefault();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.restoreFocus = false;
    var dlg = this.dialog.open(AdminDialogSetLadderComponent, dialogConfig);
    //const user = this.players.find(f => f.id == id);
    dlg.afterClosed().subscribe(
      (newladder: string) => {
        if( newladder != null && newladder != '')
        this.http.post('/admin/setladder',
          {
            UserId: id,
            LevelId: levelId,
            LadderId: this.ladderId,
            NewLadderId: newladder
          }).subscribe(
            (resp) => { this.msg = "Success"; setTimeout(() => this.msg = null, 5000); this.fetchPlayers(this.ladderId, true); },
            (err) => { this.msg = "ERROR: " + err; console.error(err); }
          )
      }
    );
  }

  public showHistory(evt: any, userId: string) {
    evt.preventDefault();
    window.open(`/history?userId=${userId}`, '_blank');
  }

}
